import { useId } from "react";
import { Link } from "react-router";

import { AuthShell } from "../auth/AuthShell.tsx";
import { PATHS } from "../routes/routes.constants.ts";
import { FORGOT_COPY } from "./passwordReset.constants.ts";
import { useForgotPasswordController } from "./useForgotPasswordController.ts";
import "./passwordReset.css";

/**
 * "Forgot password?" — one field, one button, one sentence back.
 *
 * In `AuthShell`, like sign-in and apply, because it is a signed-out screen and the
 * frame is the same 200 lines of responsive hero either way. The `.reset` wrapper
 * supplies the `--role` accent the reused field and button classes are written against;
 * `passwordReset.css` says why that cannot go on the `.role__*` classes themselves.
 *
 * Every branch below is a `kind` on `ForgotView`. There is no condition here a product
 * person would recognise as a rule — in particular nothing about whether the address has
 * an account, because the `sent` state carries no such field to read.
 */
export function ForgotPassword() {
  const id = useId();
  const { view, changeEmail, submitRequest } = useForgotPasswordController();

  return (
    <AuthShell>
      <div className="reset">
        <h2 className="auth__heading">{FORGOT_COPY.heading}</h2>

        {view.kind === "sent" ? (
          <>
            {/* The same sentence for an address with an account and one without. There
                is nothing on `view` that could make it differ. */}
            <p className="auth__subheading" role="status">
              {FORGOT_COPY.sent}
            </p>
            <p className="reset__note">{FORGOT_COPY.afterwards}</p>
          </>
        ) : (
          <>
            <p className="auth__subheading">{FORGOT_COPY.blurb}</p>

            <form
              className="reset__form"
              onSubmit={(event) => {
                event.preventDefault();
                void submitRequest();
              }}
            >
              <div className="role__field">
                <label className="role__label" htmlFor={`${id}-email`}>
                  {FORGOT_COPY.emailLabel}
                </label>
                <input
                  id={`${id}-email`}
                  className="role__input"
                  type="email"
                  autoComplete="email"
                  required
                  autoFocus
                  disabled={view.kind === "sending"}
                  placeholder="you@example.com"
                  value={view.email}
                  onChange={(event) => {
                    changeEmail(event.target.value);
                  }}
                />
              </div>

              {view.kind === "asking" && view.error !== null && (
                <p className="role__error" role="alert">
                  {view.error}
                </p>
              )}

              <button
                type="submit"
                className="role__submit"
                disabled={view.kind !== "asking" || !view.canSubmit}
              >
                {view.kind === "sending" ? FORGOT_COPY.submitting : FORGOT_COPY.submit}
              </button>
            </form>
          </>
        )}

        <p className="reset__footer">
          <Link className="login__apply-link" to={PATHS.signIn}>
            {FORGOT_COPY.backToSignIn}
          </Link>
        </p>
      </div>
    </AuthShell>
  );
}
